import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { getImageUrl } from '@/lib/polaroid-utils';

/** Validate a kind 20 event has at least one valid image URL. */
function isValidPicture(event: NostrEvent): boolean {
  if (event.kind !== 20) return false;
  return !!getImageUrl(event);
}

const PAGE_SIZE = 15;

/** Query kind 20 picture events by hashtag with infinite scroll pagination. */
export function useHashtagPictures(tag: string | undefined) {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['pictures', 'hashtag', tag ?? ''],
    queryFn: async ({ pageParam }) => {
      if (!tag) return [];
      const filter: NostrFilter = { kinds: [20], '#t': [tag], limit: PAGE_SIZE + 5 };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter],
        { signal: AbortSignal.timeout(5000) },
      );

      return events.filter(isValidPicture).slice(0, PAGE_SIZE);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    enabled: !!tag,
    staleTime: 60 * 1000,
  });
}
